'use client'

//region Imports
import React, {useRef, useState, useEffect, useCallback} from 'react'
import Moment from 'moment';
import _ from "lodash";

import {$store} from "@isudio/globals";
import {policies} from "@isudio/config";
import type {HeaderElement, FooterElement, BaseDialogProps} from "../";
import {ToolbarTop, ToolbarBottom, useDialogController} from "../";
import {renderContent} from "../../functions";
//endregion Imports

//region Types & Interfaces
interface TermsOfUseProps extends BaseDialogProps {
  dialogOptions?: any;
}

//endregion Types & Interfaces

export const TermsOfUse: React.FC<TermsOfUseProps> = ({dialogOptions}) => {
  //region Variables
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const title = dialogOptions?.title || _.get($store, `Data.termsOfUse$.value.title`, policies.termsOfUse.title);
  const className = title.toLowerCase().replace(/ /g, "-");
  const {
    version,
    content
  } = _.get($store, `Data.termsOfUse$.value`, policies.termsOfUse);
  //endregion Variables

  //region Methods & Event Handlers
  const {
    closeDialog
  } = useDialogController();

  const onScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);

    const SHOW_THRESHOLD = 10;
    const HIDE_THRESHOLD = 60;

    setIsAtBottom(prev => {
      if (!prev && distanceFromBottom <= SHOW_THRESHOLD) return true;
      if (prev && distanceFromBottom > HIDE_THRESHOLD) return false;
      return prev;
    });
  };

  const onClose = () => {
    closeDialog();
  }
  //endregion Methods & Event Handlers

  //region Effects
  useEffect(() => {
    if (!footerRef.current) return;

    const el = footerRef.current;

    const updateHeight = () => {
      setFooterHeight(el.scrollHeight);
    };

    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isAtBottom) return;

    const el = contentRef.current;
    if (!el) return;

    const t = setTimeout(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth'
      });
      clearTimeout(t);
    }, 200);
  }, [isAtBottom]);
  //endregion Effects

  //region Toolbar Elements
  const headerElements: HeaderElement[] = [
    {
      name: "title",
      type: "span",
      position: "left",
      label: title,
      style: "text-sm sm:text-md md:text-lg font-bold text-white tracking-tight md:tracking-wider"
    },
    {
      name: "subTitle",
      type: "span",
      position: "left",
      label: `(v${version} updated on ${_.getPolicyDate()})`,
      style: "text-xs sm:text-md text-gray-400 tracking-tighter"
    },
    {
      name: "close",
      type: "button",
      position: "right",
      icon: "fa-square-xmark",
      tooltip: "Close",
      style: `text-white scale-115 transition duration-200 hover:text-light-red hover:scale-125`,
      onClick: closeDialog
    }
  ];

  const footerElements: FooterElement[] = [
    {
      name: "close",
      type: "gradientButton",
      label: "Close",
      tooltip: "Close",
      style: `btn-gray-reverse`,
      onClick: onClose
    }
  ];
  //endregion Toolbar Elements

  //region HTML Elements
  const toolbarTop = (
    <ToolbarTop
      name={`tbt-${className}`}
      elements={headerElements}
      leftToolbarGap="gap-1"
    />
  );

  const toolbarBottom = (
    <ToolbarBottom
      name={`tbb-${className}`}
      elements={footerElements}
    />
  );

  const termsOfUseContent = (
    <div
      ref={contentRef}
      onScroll={onScroll}
      className={`
          content-${className} 
          flex-1 min-h-0 box-border overflow-y-auto
          bg-[radial-gradient(circle,#ffffff_0%,#d6d6d6_100%)] 
          dark:bg-[radial-gradient(circle,#030b11b8_0%,#030b11d8_100%)] 
          scrollbar-track-rounded-full scrollbar-thin 
          scrollbar-track-dim-white dark:scrollbar-track-transparent 
          scrollbar-thumb-custom-blue-600 
          scrollbar-hover:scrollbar-thumb-custom-blue-300 
          scrollbar-active:scrollbar-thumb-custom-blue-300
        `}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}>
      <div
        className="grid px-4 py-2 gap-3 text-sm md:text-md text-custom-gray-600 dark:text-pale-white leading-relaxed">
        {renderContent(content, 4)}
      </div>
    </div>
  );

  const footer = (
    <div
      style={{
        height: isAtBottom ? footerHeight : 0
      }}
      className="overflow-hidden transition-[height] duration-300 ease-in-out"
    >
      <div
        ref={footerRef}
        className="flex flex-col">
        {toolbarBottom}
      </div>
    </div>
  );
  //endregion HTML Elements

  return (
    <div
      className="w-full h-full flex flex-col rounded-sm md:rounded-md overflow-hidden">
      {toolbarTop}

      {termsOfUseContent}

      {footer}
    </div>
  )
}